import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authErrorMessage } from '@/features/auth/errors'
import {
  ForbiddenError,
  getSessionUser,
  signInWithPassword,
  signInWithTokens,
} from '@/features/auth/session'
import { openOAuthPopup, type OAuthStrategy } from '@/features/auth/oauth'
import { safeRedirect } from '@/lib/redirect'

export const Route = createFileRoute('/login')({
  validateSearch: z.object({
    redirect: z.string().optional().catch(undefined),
    error: z.enum(['forbidden']).optional().catch(undefined),
  }),
  beforeLoad: async ({ context, search }) => {
    if (search.error) return
    const user = await getSessionUser(context.queryClient).catch(() => null)
    if (user) throw redirect({ href: safeRedirect(search.redirect) })
  },
  component: LoginPage,
})

function LoginPage() {
  const search = Route.useSearch()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(
    search.error === 'forbidden' ? new ForbiddenError().message : null,
  )

  const run = async (action: () => Promise<unknown>) => {
    setPending(true)
    setError(null)
    try {
      await action()
      await navigate({ href: safeRedirect(search.redirect) })
    } catch (e) {
      setError(authErrorMessage(e))
    } finally {
      setPending(false)
    }
  }

  const oauth = (strategy: OAuthStrategy) =>
    run(async () => signInWithTokens(queryClient, await openOAuthPopup(strategy)))

  return (
    <main className="grid min-h-svh place-items-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>LOLPros.GG Administration</CardTitle>
          <CardDescription>Sign in to continue</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              const form = e.currentTarget
              const data = new FormData(form)
              const submittedUsername = String(data.get('username') ?? '')
              const submittedPassword = String(data.get('password') ?? '')
              setUsername(submittedUsername)
              setPassword(submittedPassword)
              void run(() => signInWithPassword(queryClient, submittedUsername, submittedPassword))
            }}
          >
            <div className="grid gap-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                name="username"
                autoComplete="username"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={pending}>
              Sign in
            </Button>
          </form>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" disabled={pending} onClick={() => void oauth('discord')}>
              Discord
            </Button>
            <Button variant="outline" disabled={pending} onClick={() => void oauth('twitch')}>
              Twitch
            </Button>
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </CardContent>
      </Card>
    </main>
  )
}
